import { useState, useEffect, useCallback } from 'react';

const WORKLOADS = [
    { label: 'Agentic', color: 'text-purple-400', border: 'border-purple-500/40', bg: 'bg-purple-500/10', glow: 'shadow-[0_0_12px_rgba(168,85,247,0.3)]' },
    { label: 'Real-time', color: 'text-blue-400', border: 'border-blue-500/40', bg: 'bg-blue-500/10', glow: 'shadow-[0_0_12px_rgba(59,130,246,0.3)]' },
    { label: 'Batch', color: 'text-orange-400', border: 'border-orange-500/40', bg: 'bg-orange-500/10', glow: 'shadow-[0_0_12px_rgba(249,115,22,0.3)]' },
];

const GPUS = [
    { label: 'H100', color: 'text-emerald-400' },
    { label: 'A100', color: 'text-blue-400' },
    { label: 'L40S', color: 'text-cyan-400' },
    { label: 'MI300X', color: 'text-red-400' },
];

const SUB_LABELS = ['Predict', 'Route', 'Cache', 'Scale'];

const GPU_PAIRINGS = [3, 0, 2]; // H100→Agentic mapping index... workload[i] routes to GPU[pairing[i]]

const FLOW_CSS = `
@keyframes flow-down {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(400%); }
}
`;

export const HeroArchitectureDiagram = () => {
    const [activeWorkload, setActiveWorkload] = useState(0);
    const [flowPhase, setFlowPhase] = useState(0); // 0=idle, 1=entering, 2=processing, 3=routing
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        if (isPaused) return;

        const interval = setInterval(() => {
            setFlowPhase(p => {
                if (p >= 3) {
                    setActiveWorkload(w => (w + 1) % WORKLOADS.length);
                    return 0;
                }
                return p + 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isPaused]);

    const handleMouseEnter = useCallback(() => setIsPaused(true), []);
    const handleMouseLeave = useCallback(() => setIsPaused(false), []);

    const activeGpu = GPU_PAIRINGS[activeWorkload];

    return (
        <div
            className="relative w-full max-h-[320px] lg:max-h-none select-none"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <style>{FLOW_CSS}</style>

            <div className="flex flex-col items-center gap-3 lg:gap-4">
                {/* Top layer: Workload nodes */}
                <div className="flex items-center justify-center gap-2 lg:gap-3">
                    {WORKLOADS.map((w, i) => (
                        <div
                            key={w.label}
                            className={`
                                px-3 py-1.5 lg:px-4 lg:py-2 rounded-lg border text-xs font-mono font-medium transition-all duration-500
                                ${i === activeWorkload
                                    ? `${w.border} ${w.bg} ${w.color} ${w.glow}`
                                    : 'border-white/10 bg-white/[0.03] text-gray-500'}
                            `}
                        >
                            {w.label}
                        </div>
                    ))}
                </div>

                {/* Flow line: top → middle */}
                <div className="relative w-0.5 h-8 lg:h-10 overflow-hidden bg-white/[0.06] rounded-full">
                    {flowPhase >= 1 && (
                        <div
                            className="absolute w-full h-3 rounded-full bg-gradient-to-b from-volt-400/80 to-volt-500/20"
                            style={{ animation: 'flow-down 0.8s ease-in-out infinite' }}
                        />
                    )}
                </div>

                {/* Middle layer: Forecaster box */}
                <div className={`
                    relative w-full max-w-[280px] lg:max-w-[320px] rounded-xl border-2 px-4 py-4 lg:px-5 lg:py-5 transition-all duration-500
                    ${flowPhase === 2
                        ? 'border-volt-500/60 bg-volt-500/[0.07] shadow-[0_0_30px_rgba(163,230,53,0.15)]'
                        : 'border-volt-500/20 bg-volt-500/[0.03]'}
                `}>
                    {/* Label */}
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-volt-500 text-obsidian-900 text-[10px] font-bold rounded-full whitespace-nowrap">
                        FORECASTER
                    </div>

                    {/* Sub-labels */}
                    <div className="flex items-center justify-center gap-2 lg:gap-3 mt-1">
                        {SUB_LABELS.map((label, i) => (
                            <div
                                key={label}
                                className={`
                                    text-[10px] lg:text-xs font-medium px-2 py-1 rounded-md transition-all duration-300
                                    ${flowPhase === 2 && i === flowPhase - 1
                                        ? 'text-volt-400 bg-volt-500/10'
                                        : 'text-gray-500 bg-white/[0.03]'}
                                `}
                            >
                                {label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Flow line: middle → bottom */}
                <div className="relative w-0.5 h-8 lg:h-10 overflow-hidden bg-white/[0.06] rounded-full">
                    {flowPhase >= 3 && (
                        <div
                            className="absolute w-full h-3 rounded-full bg-gradient-to-b from-volt-400/80 to-volt-500/20"
                            style={{ animation: 'flow-down 0.8s ease-in-out infinite' }}
                        />
                    )}
                </div>

                {/* Bottom layer: GPU fleet */}
                <div className="flex items-center justify-center gap-2 lg:gap-3">
                    {GPUS.map((gpu, i) => (
                        <div
                            key={gpu.label}
                            className={`
                                px-2.5 py-1.5 lg:px-3 lg:py-2 rounded-lg border text-[10px] lg:text-xs font-mono font-medium transition-all duration-500
                                ${i === activeGpu && flowPhase >= 3
                                    ? `border-volt-500/40 bg-volt-500/10 ${gpu.color} shadow-[0_0_12px_rgba(163,230,53,0.2)]`
                                    : 'border-white/10 bg-white/[0.03] text-gray-600'}
                            `}
                        >
                            {gpu.label}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
